import { useListState } from "@/hooks/use-list-state";
import { useState, useEffect, useMemo } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import { apiRequest, parseErrorStatus } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Search, Star, MapPin, ShoppingBag, UserPlus, Link2, Images, ChevronLeft, ChevronRight, X, Crown } from "lucide-react";
import type { Vendor } from "@shared/schema";
import { VENDOR_CATEGORIES } from "@shared/schema";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";

type MarketplaceReview = {
  id: number;
  overallRating: number;
  qualityRating: number | null;
  communicationRating: number | null;
  timelinessRating: number | null;
  professionalismRating: number | null;
  publicComment: string | null;
  submittedAt: string;
  editedAt: string | null;
  hostName: string | null;
};

function getInitials(name: string): string {
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function formatCategory(category: string): string {
  return category.charAt(0).toUpperCase() + category.slice(1).replace("_", " ");
}

function StarRating({ rating, reviewCount, size = "sm" }: { rating: number | null; reviewCount?: number; size?: "sm" | "md" }) {
  if (!rating) return <span className="text-xs text-gray-400">No reviews</span>;
  const starSize = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";
  const textSize = size === "md" ? "text-sm" : "text-sm";
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`${starSize} ${i <= Math.round(Number(rating)) ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`} />
      ))}
      <span className={`${textSize} text-gray-600 ml-1`} data-testid="text-aggregate-rating">
        {Number(rating).toFixed(1)}
        {reviewCount != null && (
          <span className="text-gray-400 ml-1">({reviewCount} {reviewCount === 1 ? "review" : "reviews"})</span>
        )}
      </span>
    </div>
  );
}

function formatPrice(vendor: Vendor): string | null {
  if (!vendor.defaultRateCents) return null;
  const dollars = (vendor.defaultRateCents / 100).toFixed(0);
  return vendor.rateType === "hourly" ? `$${dollars}/hr` : `$${dollars}`;
}

function WorkPhotoLightbox({
  photos,
  initialIndex,
  onClose,
}: {
  photos: string[];
  initialIndex: number;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(initialIndex);
  const prev = () => setIndex(i => (i - 1 + photos.length) % photos.length);
  const next = () => setIndex(i => (i + 1) % photos.length);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <button
        className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-1.5 hover:bg-black/70 transition-colors"
        onClick={onClose}
        aria-label="Close"
        data-testid="button-lightbox-close"
      >
        <X className="h-5 w-5" />
      </button>
      {photos.length > 1 && (
        <>
          <button
            className="absolute left-4 text-white bg-black/40 rounded-full p-1.5 hover:bg-black/70 transition-colors"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous photo"
            data-testid="button-lightbox-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            className="absolute right-4 text-white bg-black/40 rounded-full p-1.5 hover:bg-black/70 transition-colors"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next photo"
            data-testid="button-lightbox-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}
      <img
        src={photos[index]}
        alt={`Work photo ${index + 1}`}
        className="max-h-[80vh] max-w-[90vw] object-contain rounded shadow-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid="img-lightbox-photo"
      />
      {photos.length > 1 && (
        <div className="absolute bottom-4 text-white text-sm bg-black/40 px-3 py-1 rounded-full" data-testid="text-lightbox-counter">
          {index + 1} / {photos.length}
        </div>
      )}
    </div>
  );
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function VendorDetailDrawer({
  vendor,
  open,
  isLoading,
  onOpenChange,
  onAdd,
  isAdding,
  readOnly,
}: {
  vendor: Vendor | null;
  open: boolean;
  isLoading: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (vendorId: number) => void;
  isAdding: boolean;
  readOnly: boolean;
}) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  useEffect(() => { setLightboxIndex(null); }, [vendor?.id, open]);
  const { toast } = useToast();
  const handleCopyLink = async () => {
    if (!vendor) return;
    const url = `${window.location.origin}/app/operations/marketplace/${vendor.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied" });
    } catch {
      toast({ title: "Failed to copy link", variant: "destructive" });
    }
  };
  const reviewsQuery = useQuery<MarketplaceReview[]>({
    queryKey: ["/api/app/operations/marketplace", vendor?.id, "reviews"],
    queryFn: async () => {
      const res = await fetch(`/api/app/operations/marketplace/${vendor!.id}/reviews`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch reviews");
      return res.json();
    },
    enabled: open && !!vendor,
  });

  return (
    // In-flight dismissal gating (docs/dialog-dismissal-convention.md): the panel
    // is read-only except for the "Add to my directory" write, so block dismissal
    // while that add is in flight.
    <Sheet open={open} onOpenChange={(o) => { if (!o && isAdding) return; onOpenChange(o); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="sheet-vendor-detail"
        onEscapeKeyDown={(e) => { if (isAdding) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isAdding) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isAdding) e.preventDefault(); }}
      >
        {!vendor && isLoading && (
          <div className="space-y-4 pt-6" data-testid="loading-detail-vendor">
            <SheetHeader>
              <SheetTitle className="sr-only">Loading vendor</SheetTitle>
              <SheetDescription className="sr-only">
                Loading the vendor profile.
              </SheetDescription>
            </SheetHeader>
            <div className="flex flex-col items-center gap-3">
              <Skeleton className="h-32 w-32 rounded-full" />
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}
        {vendor && (
          <>
            <SheetHeader className="mb-4">
              <SheetTitle className="sr-only">{vendor.name}</SheetTitle>
              <SheetDescription className="sr-only">
                Vendor profile details including bio, service areas, ratings, and reviews.
              </SheetDescription>
            </SheetHeader>
            <div className="flex flex-col items-center text-center pb-5 border-b">
              {vendor.marketplacePhotoUrl ? (
                <img
                  src={vendor.marketplacePhotoUrl}
                  alt={vendor.name}
                  className="w-32 h-32 rounded-full object-cover mb-3"
                  data-testid={`img-detail-photo-${vendor.id}`}
                />
              ) : (
                <div
                  className="w-32 h-32 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-3xl mb-3"
                  data-testid={`text-detail-initials-${vendor.id}`}
                >
                  {getInitials(vendor.name)}
                </div>
              )}
              <div className="flex items-center justify-center gap-2">
                <h2 className="text-xl font-semibold text-gray-900" data-testid="text-detail-name">{vendor.name}</h2>
                {(vendor as any).marketplaceTier === "pro" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-accent px-2 py-0.5 text-xs font-semibold text-white" data-testid={`badge-pro-detail-${vendor.id}`}>
                    <Crown className="h-3 w-3" />
                    Pro
                  </span>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={handleCopyLink}
                  aria-label="Copy link to vendor"
                  title="Copy link"
                  data-testid={`button-copy-link-${vendor.id}`}
                >
                  <Link2 className="h-4 w-4" />
                </Button>
              </div>
              {vendor.businessName && (
                <p className="text-sm text-gray-500 mt-0.5" data-testid="text-detail-business-name">{vendor.businessName}</p>
              )}
              <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
                {vendor.category && (
                  <Badge variant="outline" data-testid="badge-detail-category">
                    {formatCategory(vendor.category)}
                  </Badge>
                )}
                {formatPrice(vendor) && (
                  <Badge variant="secondary" data-testid="text-detail-price">
                    {formatPrice(vendor)}
                  </Badge>
                )}
              </div>
              <div className="mt-3">
                <StarRating
                  rating={vendor.aggregateRating ? Number(vendor.aggregateRating) : null}
                  reviewCount={vendor.totalReviewsCount ?? undefined}
                  size="md"
                />
              </div>
              {vendor.location && (
                <div className="flex items-center gap-1 mt-2 text-xs text-gray-500" data-testid="text-detail-location">
                  <MapPin className="h-3 w-3" />
                  {vendor.location}
                </div>
              )}
            </div>

            {vendor.marketplaceBio && (
              <div className="py-4 border-b">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">About</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap" data-testid="text-detail-bio">
                  {vendor.marketplaceBio}
                </p>
              </div>
            )}

            {Array.isArray(vendor.serviceAreas) && vendor.serviceAreas.length > 0 && (
              <div className="py-4 border-b">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Service Areas</h3>
                <div className="flex flex-wrap gap-1.5" data-testid="list-detail-service-areas">
                  {(vendor.serviceAreas as string[]).map((area, i) => (
                    <Badge key={i} variant="outline" className="text-xs" data-testid={`badge-service-area-${i}`}>
                      <MapPin className="h-3 w-3 mr-1" />
                      {area}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray((vendor as any).flatRateOfferings) && (vendor as any).flatRateOfferings.length > 0 && (
              <div className="py-4 border-b">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Service Menu</h3>
                <div className="space-y-1.5" data-testid="list-detail-flat-offerings">
                  {((vendor as any).flatRateOfferings as Array<{ name: string; priceCents: number }>).map((offering, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-sm" data-testid={`row-detail-flat-offering-${i}`}>
                      <span className="text-gray-700 min-w-0 break-words">{offering.name}</span>
                      <span className="font-medium text-gray-900 shrink-0">${(offering.priceCents / 100).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray((vendor as any).workPhotoUrls) && (vendor as any).workPhotoUrls.length > 0 && (
              <div className="py-4 border-b">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
                  <Images className="h-3.5 w-3.5" />
                  Work Photos
                </h3>
                <div className="grid grid-cols-3 gap-1.5" data-testid="list-detail-work-photos">
                  {((vendor as any).workPhotoUrls as string[]).map((url, i) => (
                    <div key={i} className="relative">
                      <button
                        className="aspect-square rounded overflow-hidden focus:outline-none focus:ring-2 focus:ring-brand w-full"
                        onClick={() => setLightboxIndex(i)}
                        data-testid={`button-work-photo-${i}`}
                      >
                        <img
                          src={url}
                          alt={`Work photo ${i + 1}`}
                          className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                        />
                      </button>
                      {i === 0 && (
                        <span
                          className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 rounded pointer-events-none"
                          data-testid="badge-cover-photo"
                        >
                          Cover
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {lightboxIndex !== null && Array.isArray((vendor as any).workPhotoUrls) && (
              <WorkPhotoLightbox
                photos={(vendor as any).workPhotoUrls as string[]}
                initialIndex={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
              />
            )}

            <div className="py-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Track Record</h3>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
                <div>
                  <div className="text-gray-500 text-xs">Jobs completed</div>
                  <div className="font-medium text-gray-900" data-testid="text-detail-jobs-completed">
                    {vendor.totalJobsCompleted || 0}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs">Total reviews</div>
                  <div className="font-medium text-gray-900" data-testid="text-detail-review-count">
                    {vendor.totalReviewsCount || 0}
                  </div>
                </div>
              </div>
            </div>

            <div className="py-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Reviews</h3>
              {reviewsQuery.isLoading && (
                <div className="space-y-2">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              )}
              {!reviewsQuery.isLoading && (reviewsQuery.data?.length ?? 0) === 0 && (
                <p className="text-sm text-gray-400" data-testid="text-detail-no-reviews">No reviews yet</p>
              )}
              {!reviewsQuery.isLoading && (reviewsQuery.data?.length ?? 0) > 0 && (
                <div className="space-y-3" data-testid="list-detail-reviews">
                  {reviewsQuery.data!.map(review => (
                    <div
                      key={review.id}
                      className="rounded-md border bg-gray-50 p-3"
                      data-testid={`review-item-${review.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <StarRating rating={review.overallRating} />
                        <span className="text-xs text-gray-400">
                          {new Date(review.submittedAt).toLocaleDateString()}
                        </span>
                      </div>
                      {review.publicComment && (
                        <p className="text-sm text-gray-700 mt-1" data-testid={`text-review-comment-${review.id}`}>
                          {review.publicComment}
                        </p>
                      )}
                      {review.hostName && (
                        <p className="text-xs text-gray-500 mt-2" data-testid={`text-review-author-${review.id}`}>
                          — {review.hostName}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-background border-t">
              <Button
                className="w-full"
                onClick={() => onAdd(vendor.id)}
                disabled={readOnly || isAdding}
                data-testid={`button-detail-add-vendor-${vendor.id}`}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add to my directory
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function AppOperationsMarketplace() {
  const queryClient = useQueryClient();
  usePageTitle("Vendor Marketplace");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [, params] = useRoute<{ vendorId: string }>("/app/operations/marketplace/:vendorId");
  const rawUrlVendorId = params?.vendorId ?? null;
  const validUrlVendorId =
    rawUrlVendorId !== null && /^\d+$/.test(rawUrlVendorId) ? Number(rawUrlVendorId) : null;
  const hasInvalidVendorIdInUrl = rawUrlVendorId !== null && validUrlVendorId === null;

  const [search, setSearch] = useListState("marketplace:search", "");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [minRating, setMinRating] = useState("");
  const [sortBy, setSortBy] = useState("");

  const debouncedCity = useDebounce(city, 400);

  const vendorsQuery = useQuery<Vendor[]>({
    queryKey: ["/api/app/operations/marketplace", category, debouncedCity, minRating, sortBy],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (category && category !== "all") params.set("category", category);
      if (debouncedCity) params.set("city", debouncedCity);
      if (minRating && minRating !== "any") params.set("minRating", minRating);
      if (sortBy && sortBy !== "default") params.set("sortBy", sortBy);
      const qs = params.toString();
      const res = await fetch(`/api/app/operations/marketplace${qs ? `?${qs}` : ""}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch marketplace");
      return res.json();
    },
  });

  // Fetch the URL-targeted vendor directly so deep links open even when the
  // vendor is filtered out of the current list (or the list hasn't loaded yet).
  const urlVendorQuery = useQuery<Vendor>({
    queryKey: ["/api/app/operations/marketplace", validUrlVendorId],
    queryFn: async () => {
      const res = await fetch(`/api/app/operations/marketplace/${validUrlVendorId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch vendor");
      return res.json();
    },
    enabled: validUrlVendorId !== null,
  });

  const selectedVendor = useMemo<Vendor | null>(() => {
    if (validUrlVendorId === null) return null;
    const fromList = (vendorsQuery.data || []).find(v => v.id === validUrlVendorId);
    return fromList || urlVendorQuery.data || null;
  }, [validUrlVendorId, vendorsQuery.data, urlVendorQuery.data]);

  const openVendor = (vendorId: number) => {
    setLocation(`/app/operations/marketplace/${vendorId}`);
  };

  const closeVendor = () => {
    setLocation("/app/operations/marketplace");
  };

  // Clear an invalid vendor id from the URL once we know the fetch failed
  useEffect(() => {
    if (validUrlVendorId !== null && urlVendorQuery.isError) {
      toast({ title: "Vendor not found", variant: "destructive" });
      closeVendor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validUrlVendorId, urlVendorQuery.isError]);

  // Normalize non-numeric vendor ids in the URL (e.g. /marketplace/abc) back
  // to the base marketplace route so the page stays usable.
  useEffect(() => {
    if (hasInvalidVendorIdInUrl) {
      toast({ title: "Vendor not found", variant: "destructive" });
      closeVendor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInvalidVendorIdInUrl]);

  const addMutation = useMutation({
    mutationFn: async (vendorId: number) => {
      return apiRequest("POST", `/api/app/operations/marketplace/${vendorId}/add`);
    },
    onSuccess: () => {
      toast({ title: "Vendor added to your directory" });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/getting-started"] });
      closeVendor();
    },
    onError: (e: any) => {
      const status = parseErrorStatus(e);
      if (status === 409) {
        toast({ title: "Already on your roster", description: "This vendor is already on your roster.", variant: "destructive" });
        return;
      }
      if (status === 404) {
        toast({ title: "Vendor not found", description: "This vendor could not be found. It may have been removed.", variant: "destructive" });
        return;
      }
      toast({ title: "Failed to add vendor", variant: "destructive" });
    },
  });

  const vendors = (vendorsQuery.data || []).filter(v =>
    !search || String(v.name ?? "").toLowerCase().includes(search.toLowerCase()) ||
    String(v.businessName ?? "").toLowerCase().includes(search.toLowerCase()) ||
    String(v.marketplaceBio ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AppLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="page-marketplace">
        <ViewOnlyBanner area="maintenance" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" data-testid="text-page-title" data-tour="vendor-marketplace-heading">Vendor Marketplace</h1>
            <p className="text-sm text-gray-500 mt-1">Find and add verified service providers to your vendor directory</p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="vendor-marketplace" />
            <GuideButton pageKey="vendor-marketplace" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="vendor-marketplace" />

        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search vendors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="input-search"
            />
          </div>
          <div className="relative min-w-[180px]">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
            <Input
              className="pl-9"
              placeholder="City / area..."
              value={city}
              onChange={(e) => setCity(e.target.value)}
              data-testid="input-city"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[180px]" data-testid="select-category" data-tour="vendor-marketplace-filters">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {VENDOR_CATEGORIES.map(c => (
                <SelectItem key={c} value={c}>
                  {formatCategory(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={minRating} onValueChange={setMinRating}>
            <SelectTrigger className="w-[150px]" data-testid="select-min-rating">
              <SelectValue placeholder="Min Rating" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any Rating</SelectItem>
              <SelectItem value="3">3+ Stars</SelectItem>
              <SelectItem value="4">4+ Stars</SelectItem>
              <SelectItem value="4.5">4.5+ Stars</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px]" data-testid="select-sort-by">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Highest Rated</SelectItem>
              <SelectItem value="price_asc">Lowest Price</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {vendorsQuery.isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-48 w-full" />)}
          </div>
        )}

        {vendors.length === 0 && !vendorsQuery.isLoading && (
          <Card>
            <CardContent className="py-12 text-center">
              <ShoppingBag className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No marketplace vendors found</p>
              <p className="text-sm text-gray-400 mt-1">Vendors who opt into the marketplace will appear here</p>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-tour="vendor-marketplace-list">
          {vendors.map(vendor => (
            <Card
              key={vendor.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openVendor(vendor.id)}
              data-testid={`card-marketplace-vendor-${vendor.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  {vendor.marketplacePhotoUrl ? (
                    <img
                      src={vendor.marketplacePhotoUrl}
                      alt={vendor.name}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      data-testid={`img-vendor-photo-${vendor.id}`}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-semibold text-lg flex-shrink-0">
                      {getInitials(vendor.name)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-medium text-gray-900 truncate">{vendor.name}</h3>
                      {(vendor as any).marketplaceTier === "pro" && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-brand-accent px-1.5 py-0.5 text-[10px] font-semibold text-white shrink-0" data-testid={`badge-pro-${vendor.id}`}>
                          <Crown className="h-2.5 w-2.5" />
                          Pro
                        </span>
                      )}
                    </div>
                    {vendor.businessName && <p className="text-sm text-gray-500 truncate">{vendor.businessName}</p>}
                    {vendor.category && (
                      <Badge variant="outline" className="text-xs mt-0.5">
                        {formatCategory(vendor.category)}
                      </Badge>
                    )}
                  </div>
                </div>

                <StarRating
                  rating={vendor.aggregateRating ? Number(vendor.aggregateRating) : null}
                  reviewCount={vendor.totalReviewsCount ?? undefined}
                />

                {vendor.marketplaceBio && (
                  <p className="text-sm text-gray-600 mt-2 line-clamp-2">{vendor.marketplaceBio}</p>
                )}

                {vendor.location && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                    <MapPin className="h-3 w-3" />
                    {vendor.location}
                  </div>
                )}

                {Array.isArray((vendor as any).workPhotoUrls) && (vendor as any).workPhotoUrls.length > 0 && (
                  <div className="flex gap-1 mt-3 overflow-hidden" data-testid={`strip-work-photos-${vendor.id}`}>
                    {((vendor as any).workPhotoUrls as string[]).slice(0, 3).map((url: string, i: number) => (
                      <img
                        key={i}
                        src={url}
                        alt={`Work photo ${i + 1}`}
                        className="h-14 w-14 rounded object-cover flex-shrink-0"
                        data-testid={`img-card-work-photo-${vendor.id}-${i}`}
                      />
                    ))}
                    {(vendor as any).workPhotoUrls.length > 3 && (
                      <div
                        className="h-14 w-14 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-medium text-gray-500"
                        data-testid={`text-card-more-photos-${vendor.id}`}
                      >
                        +{(vendor as any).workPhotoUrls.length - 3}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between mt-4 pt-3 border-t">
                  <div className="text-xs text-gray-400 space-y-0.5">
                    <div>{vendor.totalJobsCompleted || 0} jobs completed</div>
                    {formatPrice(vendor) && (
                      <div className="font-medium text-gray-600" data-testid={`text-price-${vendor.id}`}>
                        {formatPrice(vendor)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        openVendor(vendor.id);
                      }}
                      data-testid={`button-view-details-${vendor.id}`}
                    >
                      View details
                    </Button>
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        addMutation.mutate(vendor.id);
                      }}
                      disabled={readOnly || (addMutation.isPending)}
                      data-testid={`button-add-vendor-${vendor.id}`}
                      data-tour="marketplace-vendor-add"
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <VendorDetailDrawer
          vendor={selectedVendor}
          open={validUrlVendorId !== null}
          isLoading={validUrlVendorId !== null && urlVendorQuery.isLoading && !selectedVendor}
          onOpenChange={(open) => { if (!open) closeVendor(); }}
          onAdd={(id) => addMutation.mutate(id)}
          isAdding={addMutation.isPending}
          readOnly={readOnly}
        />
      </div>
    </AppLayout>
  );
}
